/**
 * Verification Component
 * Where the user enters the verification code for either email or phone
 */
'use client'

import React, { useEffect, useRef } from 'react'

import { BorderedButton, Input, SolidButton } from '.'
import { useCooldown } from '../hooks/useCooldown'
import { EmailIcon, Envelope, PhoneIcon, PhoneSmall } from '../icons'

interface VerificationFormProps extends React.InputHTMLAttributes<HTMLInputElement> {
  authMode: 'email_otp' | 'sms_otp'
  username: string
  challengeCode: string
  setChallengeCode: (code: string) => void
  handleMFAVerify: (e: React.FormEvent<HTMLFormElement>) => void
  handleMFAChallenge: (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => void
  handleOTPComplete: (code: string) => void
  loading: boolean
  error: string
  setError: (error: string) => void
  resetMFAFlow: () => void
}

export default function VerificationForm ({
  authMode,
  username,
  challengeCode,
  setChallengeCode,
  handleMFAVerify,
  handleMFAChallenge,
  handleOTPComplete,
  loading,
  error,
  setError,
  resetMFAFlow,
  ...props
}: VerificationFormProps): React.JSX.Element {
  const isEmail = authMode === 'email_otp'
  const isPhone = authMode === 'sms_otp'

  const resendCooldown = useCooldown(10)
  const isVerifying = loading && !resendCooldown.isInCooldown
  const hasStartedCooldown = useRef(false)

  // Start cooldown automatically when component mounts
  useEffect(() => {
    if (!hasStartedCooldown.current) {
      resendCooldown.startCooldown()
      hasStartedCooldown.current = true
    }
  }, [resendCooldown])

  const handleResendCode = (e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (resendCooldown.isInCooldown || loading) return

    resendCooldown.startCooldown()
    handleMFAChallenge(e)
  }

  return (
    <div>
      {isEmail && <EmailIcon width={91} className="mx-auto" />}
      {isPhone && <PhoneIcon width={42} className="mx-auto" />}

      <h2 className="mt-6 text-center text-2xl font-extrabold text-neutrals-800" data-testid="title">
        Check your {isEmail ? 'email' : 'phone'}
      </h2>

      <div className="mt-4 text-center text-sm font-medium">
        <p className="text-neutrals-600">We've sent an 8-digit code to</p>
        <p className="text-neutrals-900">{username}</p>
      </div>

      <form className="mt-4 space-y-4" onSubmit={handleMFAVerify}>
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-neutrals-800">
            Code
          </label>
          <Input
            id="code"
            name="code"
            type="text"
            autoComplete="off"
            value={challengeCode}
            onChange={e => {
              setChallengeCode(e.target.value)
              setError('')
            }}
            disabled={false}
            error={!!error}
            className="mt-2"
            placeholder="Enter the 8-digit code"
            data-testid="code-input"
          />

          {error && (
            <div className="mt-2 font-medium text-xs text-error" data-testid="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <SolidButton
            type="submit"
            disabled={loading || challengeCode.length !== 8 || !!error}
            className={`h-[44px] ${isVerifying ? 'disabled:bg-primary-800 disabled:opacity-100' : ''}`}
            data-testid="verify-code-button"
          >
            {isVerifying
              ? (
                <div className="flex items-center justify-center space-x-1">
                  <div className="flex space-x-1">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '200ms', animationDuration: '1s' }} />
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '400ms', animationDuration: '1s' }} />
                  </div>
                </div>
                )
              : 'Verify code'}
          </SolidButton>

          <p className="text-sm text-neutrals-600 text-center">This code will expire in 15 minutes</p>
        </div>

        <div className="my-5 border-t border-neutrals-300 w-[calc(100%+2rem)] relative -left-4" />

        <p className="text-sm text-neutrals-600 text-center">Didn't receive the code?</p>

        <div className="flex flex-wrap gap-2 justify-center">
          <BorderedButton
            className="flex items-center min-w-[163px] flex-1 justify-center"
            onClick={handleResendCode}
            disabled={resendCooldown.isInCooldown}
            data-testid="resend-code-button"
          >
            {isEmail && <Envelope width={24} height={24} className="mr-1" />}
            {isPhone && <PhoneSmall width={24} height={24} className="mr-1" />}
            {resendCooldown.isInCooldown ? `Wait ${resendCooldown.cooldownSeconds}s` : 'Re-send code'}
          </BorderedButton>

          <BorderedButton className="flex items-center min-w-[163px] flex-1 justify-center" onClick={resetMFAFlow} data-testid="send-via-button">
            {isEmail && <PhoneSmall width={24} height={24} className="mr-1" />}
            {isPhone && <Envelope width={24} height={24} className="mr-1" />}
            Send via {isEmail ? 'SMS' : 'email'}
          </BorderedButton>
        </div>

        <div className="bg-secondary-25 mt-4 p-6 pt-5 rounded-lg" data-testid="help-text">
          <h3 className="text-lg font-semibold text-neutrals-800">Having trouble?</h3>
          {isEmail && <p className="text-sm font-medium text-neutrals-800 leading-relaxed" data-testid="help-text-email">Check your spam folder or try requesting a new code via text.</p>}
          {isPhone && <p className="text-sm font-medium text-neutrals-800 leading-relaxed" data-testid="help-text-phone">Try requesting a new code via email.</p>}
        </div>
      </form>
    </div>
  )
}
